import { IMenu } from "@/components/navigation/menu.interface";
import HttpService from "@/services/http.service";
import PermissionService from "@/services/permission.service";
import UtilService from "@/services/util.service";
import { defineStore } from "pinia";

const HOLIDAY_MENU_PATH = '/drivers/calendar-overview';
const MOBILE_DEVICES_MENU_PATH = '/mobile-devices';

export const useAppLayoutStore = defineStore({
    id: 'appLayout',
    state: () => ({
        menuItems: [] as IMenu[],
    }),
    actions: {
        async getMenuItems() {
            if (this.menuItems.length > 0) {
                await this.refreshHolidayRequestsBadge();
                await this.refreshMobileDevicesBadge();
                return;
            }
            const response = await fetch(`/common/menu.json`);
            const data = await response.json();
            const tenantType = await UtilService.getTenantType();
            if (!data[tenantType]) {
                console.error(`Tenant type (${tenantType}) not found in menu.json`);
                return;
            }
            const all_menus = [...data.common, ...data[tenantType]];
            const unfiltered_menus = [...all_menus];
            const user_roles = await PermissionService.getRoles();
            const loggedUser = await PermissionService.getUser();
            const tenant = await PermissionService.getTenantKey();
            const isAgency = await UtilService.isHrAgency();
            let app_menu: IMenu[] = [];
            for (let am of unfiltered_menus) {
                if (am.tenants && am.tenants.length > 0) {
                    if (!am.tenants.includes(tenant)) {
                        continue;
                    }
                }
                if (isAgency && am.always_agency) {
                    app_menu.push(am);
                    continue;
                }

                if (am.path === MOBILE_DEVICES_MENU_PATH && loggedUser?.is_owner) {
                    app_menu.push(am);
                    continue;
                }

                if (am.permission) {
                    // 'none' explicitly denies a role, even if another (e.g. implied) role grants access
                    if (am.permission !== 'all' && Object.entries(am.permission).some(([role, access]) => access === 'none' && user_roles.includes(role))) {
                        continue;
                    }
                    if (am.permission === 'all') {
                        app_menu.push(am);
                    } else if (Object.entries(am.permission).some(([role, access]) => access === 'full' && user_roles.includes(role))) {
                        app_menu.push(am);
                    }
                } else {
                    app_menu.push(am);
                }
            }
            this.menuItems = app_menu;
            await this.refreshHolidayRequestsBadge();
            await this.refreshMobileDevicesBadge();
        },

        async refreshHolidayRequestsBadge() {
            const holidayMenuItem = this.menuItems.find((item) => item.path === HOLIDAY_MENU_PATH);

            if (!holidayMenuItem) {
                return;
            }

            try {
                const response = await HttpService.get('/drivers/holiday-requests?status=requested');
                holidayMenuItem.badgeCount = Array.isArray(response?.rows) ? response.rows.length : 0;
            } catch (error) {
                console.error('Error refreshing holiday requests badge:', error);
                holidayMenuItem.badgeCount = 0;
            }
        },
        async refreshMobileDevicesBadge() {
            const menuItem = this.menuItems.find((item) => item.path === MOBILE_DEVICES_MENU_PATH);
            if (!menuItem) return;
            try {
                const response = await HttpService.get('/mobile-device-approvals/pending-count');
                menuItem.badgeCount = Number(response?.count || 0);
            } catch (_) {
                menuItem.badgeCount = 0;
            }
        }
    }
});
